/**
 * TargetedMessagingProvider.ts
 *
 * Module for targeted messaging campaign definition provider.
 */

import { Api } from "@ms-ofb/floodgate-shared-typings";
import { FilterValidCampaignDefinitions } from "@ms-ofb/officefloodgatecore/dist/src/Campaign/CampaignDefinitionProvider";
import { CampaignDefinitions } from "@ms-ofb/officefloodgatecore/dist/src/Governance/CampaignDefinitions";
import { getDynamicSetting, SettingKey } from "@ms-ofb/officepersonalization/dist/lib/Configuration/DynamicConfiguration";
import { TMS_CAMPAIGN_LOAD_TIMEOUT } from "@ms-ofb/officepersonalization/dist/lib/TargetedMessaging/TargetedMessagingConstants";
import * as Logging from "../Logging/Logging";
import { createTimeoutPromise } from "../Utils";

import * as AssetsProvider from "./AssetsProvider";

export class TargetedMessagingCampaignDefinitionProvider implements Api.ICampaignDefinitionProvider {
	private loadAsyncTriggered = false;
	private campaignDefinitions: Api.ICampaignDefinition[] = [];

	public load(): Api.ICampaignDefinition[] {
		if (!this.loadAsyncTriggered) {
			this.loadAsync();
			this.loadAsyncTriggered = true;
		}

		return this.campaignDefinitions || [];
	}

	public loadAsync(): Promise<Api.ICampaignDefinition[]> {
		const requestStartTime = Date.now();
		const campaignDefinitionsPromise = AssetsProvider.get().getCampaignDefinitions();
		campaignDefinitionsPromise.then((response) => {
			this.loadCampaignDefinitions(response);
			this.loadAsyncTriggered = true;
		});

		const timeoutMilliseconds = getDynamicSetting(SettingKey.tmsLoadTimeout, TMS_CAMPAIGN_LOAD_TIMEOUT);
		const timeoutPromise = createTimeoutPromise(timeoutMilliseconds, campaignDefinitionsPromise);
		return new Promise((resolve, reject) => {
			timeoutPromise.then((response) => {
					this.loadCampaignDefinitions(response);
					this.loadAsyncTriggered = true;
					resolve(this.campaignDefinitions);
				}
			).catch(
				// might have timed out or getCampaignDefinitions might have failed
				// In either case we resolve with cached content
				(error) => {
					const requestTimeElapsed = Date.now() - requestStartTime;
					if (this.campaignDefinitions.length <= 0) { //get campaign def failed
						this.loadCampaignDefinitions(AssetsProvider.get().getCachedCampaignDefinitions());
					}

					Logging.getLogger().logEvent(
						Logging.EventIds.FetchCampaignDefinition.Service.Error.VALUE,
						Logging.LogLevel.Error,
						{
							ErrorMessage: error?.toString(),
							TimeMilliseconds: requestTimeElapsed,
							Count: this.campaignDefinitions.length,
						}
					);
					
					resolve(this.campaignDefinitions);
				}
			).catch(
				// handle any errors from catch block
				(error) => {
					resolve(this.campaignDefinitions);
				}
			);
		});
	}
	
	public hasDataChanged() {
		// Not implemented - return false
		return false;
	}

	private loadCampaignDefinitions(response: CampaignDefinitions) {
		const definitions = response && response.campaigns;
		if (definitions) {
			this.campaignDefinitions = FilterValidCampaignDefinitions(definitions).result;
		}
	}
}
